import { createContext, useState, useEffect, useCallback } from "react";
import {
    getProfile,
    getCart,
    addToCart,
    updateCartItemQuantity,
    clearCart,
    fetchRestaurants,
    fetchGroceryStores,
    fetchCloudKitchens,
    updateUserProfile,
    changeUserPassword,
    deleteGuestCart
} from "../api/apiService";

export const StoreContext = createContext(null);

const getGuestId = () => {
    let guestId = localStorage.getItem("guest_id");
    if (!guestId) {
        guestId = `guest_${new Date().getTime()}_${Math.random().toString(36).substring(2, 9)}`;
        localStorage.setItem("guest_id", guestId);
    }
    return guestId;
};

export const StoreProvider = (props) => {
    const [cart, setCart] = useState({ items: [] });
    const [token, setToken] = useState(localStorage.getItem("customer_token") || "");
    const [user, setUser] = useState(() => JSON.parse(localStorage.getItem("user") || null));
    const [food_list, setFoodList] = useState([]);
    const [vendor_names, setVendorNames] = useState({});
    const [loading, setLoading] = useState(true);
    const [authLoading, setAuthLoading] = useState(false);
    const [pendingAddItem, setPendingAddItem] = useState(null);
    const [showVendorConflictModal, setShowVendorConflictModal] = useState(false);
    const [conflictingVendorName, setConflictingVendorName] = useState('');
    const [guestVerified, setGuestVerifiedState] = useState(localStorage.getItem("guest_verified") === "true");
    const [guestEmail, setGuestEmailState] = useState(localStorage.getItem("guest_email") || "");
    // +++ ADD GUEST PHONE STATE +++
    const [guestPhone, setGuestPhoneState] = useState(localStorage.getItem("guest_phone") || "");

    // UI error state for cart actions
    const [cartError, setCartError] = useState("");
    const [cartSuccess, setCartSuccess] = useState("");

    const deliveryCharge = 50;

    // Centralized and robust cart fetching logic
    const fetchCart = useCallback(async (currentToken, currentUser) => {
        try {
            let identifier = {};
            if (currentUser && currentToken) {
                identifier = { user_id: currentUser._id };
            } else if (user && token) {
                identifier = { user_id: user._id };
            } else {
                identifier = { guest_id: getGuestId() };
            }
            const response = await getCart(identifier);
            if (response.data.success) {
                setCart(response.data.cart || { items: [] });
            }
        } catch (error) {
            setCartError("Failed to fetch cart.");
            console.error("Failed to fetch cart:", error);
        }
    }, [user, token]);

    // Streamlined and robust user loading and cart merging logic
    const loadUserData = useCallback(async (authToken) => {
        setAuthLoading(true);
        try {
            const profileResponse = await getProfile(authToken);
            if (!profileResponse.data.success) {
                throw new Error("Failed to fetch profile");
            }
            const fetchedUser = profileResponse.data.user;
            setUser(fetchedUser);
            localStorage.setItem("user", JSON.stringify(fetchedUser));

            const guestId = localStorage.getItem("guest_id");
            if (guestId) {
                const guestCartResponse = await getCart({ guest_id: guestId });
                if (guestCartResponse.data.success && guestCartResponse.data.cart.items.length > 0) {
                    for (const [index, item] of guestCartResponse.data.cart.items.entries()) {
                        await addToCart({
                            user_id: fetchedUser._id,
                            item_id: item.item_id || item._id,
                            quantity: item.quantity,
                            vendor_id: guestCartResponse.data.cart.vendor_id,
                            vendor_type: guestCartResponse.data.cart.vendor_type,
                            name: item.name,
                            price_per_unit: item.price_per_unit,
                            image: item.image,
                            discount: item.discount_applied || 0,
                            variant_id: item.variant_id,
                            spicyLevel: item.spicyLevel,
                            specialInstructions: item.notes,
                            force: index === 0,
                        });
                    }
                    await deleteGuestCart({ guest_id: guestId });
                }
                localStorage.removeItem("guest_id");
                localStorage.removeItem("guest_verified");
                localStorage.removeItem("guest_email");
                localStorage.removeItem("guest_phone"); // +++ ADD THIS LINE +++
                setGuestVerifiedState(false);
                setGuestEmailState("");
                setGuestPhoneState(""); // +++ ADD THIS LINE +++
            }
            await fetchCart(authToken, fetchedUser);
        // } catch (error) {
        //     localStorage.removeItem("customer_token");
        //     localStorage.removeItem("user");
        //     setToken("");
        //     setUser(null);
        //     await fetchCart(null, null);
        // } finally {
        
        } catch (error) {
            console.error("loadUserData failed:", error);
            throw error;
        } finally {
            setAuthLoading(false);
        }
    }, [fetchCart]);

    // The following functions are unchanged (fine as-is)
    const fetchFoodList = useCallback(async () => {
        try {
            const [restaurantsRes, groceriesRes, cloudKitchensRes] = await Promise.all([
                fetchRestaurants(), fetchGroceryStores(), fetchCloudKitchens()
            ]);
            const combinedList = [];
            const vendorNamesMap = {};
            restaurantsRes.data.restaurants.forEach(r => {
                vendorNamesMap[r._id] = r.name;
                (r.items || []).forEach(item => combinedList.push({ ...item, vendor_id: r._id, vendor_name: r.name, vendor_type: 'restaurant' }));
            });
            groceriesRes.data.groceryStores.forEach(g => {
                vendorNamesMap[g._id] = g.name;
                (g.items || []).forEach(item => combinedList.push({ ...item, vendor_id: g._id, vendor_name: g.name, vendor_type: 'grocery_store' }));
            });
            cloudKitchensRes.data.cloudKitchens.forEach(c => {
                vendorNamesMap[c._id] = c.name;
                (c.items || []).forEach(item => combinedList.push({ ...item, vendor_id: c._id, vendor_name: c.name, vendor_type: 'cloud_kitchen' }));
            });
            setFoodList(combinedList);
            setVendorNames(vendorNamesMap);
        } catch (error) {
            setCartError("Failed to fetch food list.");
        }
    }, []);
    const addItemToCart = async (itemData, force = false) => {
        setCartError("");
        setCartSuccess("");
        if (cart.items && cart.items.length > 0 && cart.vendor_id !== itemData.vendor_id && !force) {
            setConflictingVendorName(vendor_names[cart.vendor_id] || "another vendor");
            setPendingAddItem(itemData);
            setShowVendorConflictModal(true);
            return;
        }
        try {
            let payload = {
                ...itemData,
                vendor_id: itemData.vendor_id,
                force
            };
            if (user && user._id) {
                payload.user_id = user._id;
                delete payload.guest_id;
            } else {
                const guestId = getGuestId();
                payload.guest_id = guestId;
                delete payload.user_id;
            }
            const response = await addToCart(payload);
            if (response.data.success) {
                setCart(response.data.cart);
                setCartSuccess(`${itemData.name} added to cart!`);
            } else {
                setCartError(response.data.message || "Failed to add item.");
            }
        } catch (error) {
            setCartError(error.response?.data?.message || "An error occurred.");
        }
    };
    const updateItemQuantity = async (cartItemId, newQuantity) => {
        setCartError("");
        setCartSuccess("");
        try {
            let identifier = {};
            if (user && user._id) {
                identifier = { user_id: user._id };
            } else {
                const guestId = getGuestId();
                if (guestId) identifier = { guest_id: guestId };
            }
            if (!identifier.user_id && !identifier.guest_id) {
                setCartError("No user or guest ID found for cart update.");
                return;
            }
            const response = await updateCartItemQuantity({ ...identifier, cart_item_id: cartItemId, new_quantity: newQuantity });
            if (response.data.success) {
                setCart(response.data.cart);
                setCartSuccess("Item quantity updated.");
            } else {
                setCartError(response.data.message || "Failed to update item quantity.");
            }
        } catch (error) {
            setCartError("Failed to update item quantity.");
        }
    };
    const clearUserCart = async () => {
        setCartError("");
        setCartSuccess("");
        try {
            let identifier = {};
            if (user && user._id) {
                identifier = { user_id: user._id };
            } else {
                identifier = { guest_id: getGuestId() };
            }
            await clearCart(identifier);
            setCart({ items: [], vendor_id: null, vendor_type: null });
            setCartSuccess("Cart cleared.");
        } catch (error) {
            setCartError("Failed to clear cart.");
        }
    };
    const setGuestEmail = (email) => {
        setGuestEmailState(email);
        localStorage.setItem("guest_email", email);
    };

    // +++ ADD SETTER FOR GUEST PHONE +++
    const setGuestPhone = (phone) => {
        setGuestPhoneState(phone);
        localStorage.setItem("guest_phone", phone);
    };

    const setGuestVerified = (isVerified) => {
        setGuestVerifiedState(isVerified);
        localStorage.setItem("guest_verified", isVerified ? "true" : "false");
    };
    const updateProfile = async (profileData) => {
        try {
            const response = await updateUserProfile(profileData);
            if (response.data.success) {
                setUser(response.data.user);
                localStorage.setItem("user", JSON.stringify(response.data.user));
                return { success: true };
            }
            return { success: false, error: response.data.message };
        } catch (err) {
            return { success: false, error: err.response?.data?.message || "Update failed." };
        }
    };
    const changePassword = async ({ oldPassword, newPassword }) => {
        try {
            const response = await changeUserPassword({ oldPassword, newPassword });
            if (response.data.success) {
                return { success: true };
            }
            return { success: false, error: response.data.message };
        } catch (err) {
            return { success: false, error: err.response?.data?.message || "Password change failed." };
        }
    };
    const logout = async () => {
        localStorage.removeItem("customer_token");
        localStorage.removeItem("user");
        localStorage.removeItem("guest_verified");
        localStorage.removeItem("guest_email");
        localStorage.removeItem("guest_phone"); // +++ ADD THIS LINE +++
        setToken("");
        setUser(null);
        setGuestVerifiedState(false);
        setGuestEmailState("");
        setGuestPhoneState(""); // +++ ADD THIS LINE +++
        setCart({ items: [] });
        await fetchCart(null, null);
    };
    useEffect(() => {
        const initialLoad = async () => {
            setLoading(true);
            await fetchFoodList();
            const storedToken = localStorage.getItem("customer_token");
            if (storedToken) {
                setToken(storedToken);
                await loadUserData(storedToken);
            } else {
                await fetchCart(null, null);
            }
            setLoading(false);
        };
        initialLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const contextValue = {
        cart, setCart, addItemToCart, updateItemQuantity, clearUserCart,
        token, setToken, user, setUser,
        food_list, vendor_names, loading, 
        authLoading,
        showVendorConflictModal, setShowVendorConflictModal, conflictingVendorName, pendingAddItem, setPendingAddItem,
        currency: cart?.currencySymbol || '₹',
        deliveryCharge,
        logout, loadUserData,
        guestVerified, setGuestVerified, guestEmail, setGuestEmail,
        // +++ ADD GUEST PHONE TO CONTEXT +++
        guestPhone, setGuestPhone,
        updateProfile, changePassword,
        cartError, setCartError, cartSuccess, setCartSuccess // <-- expose error/success for UI
    };

    return (
        <StoreContext.Provider value={contextValue}>
            {props.children}
        </StoreContext.Provider>
    );
};

export default StoreProvider;
